/**
 * Pending Drivers Approval Page
 * صفحة الموافقة على السائقين الجدد
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';

const I = {
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
};

const PendingDriversPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPendingDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/tenant/drivers/pending-approval');
      setDrivers(data || []);
    } catch (err) {
      showError('فشل تحميل السائقين المعلقين');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadPendingDrivers(); }, [loadPendingDrivers]);

  const handleAction = async () => {
    if (!selectedDriver || !actionType) return;
    setSubmitting(true);
    try {
      await apiCall(`/tenant/drivers/${selectedDriver.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ action: actionType, notes })
      });
      success(actionType === 'approve' ? 'تم قبول السائق بنجاح' : 'تم رفض السائق');
      setShowModal(false);
      setSelectedDriver(null);
      setActionType(null);
      setNotes('');
      loadPendingDrivers();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (driver, action) => {
    setSelectedDriver(driver);
    setActionType(action);
    setNotes('');
    setShowModal(true);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <button className="btn btn--ghost" onClick={() => navigate('/tenant/drivers')} style={{ marginLeft: '-8px', marginBottom: '8px' }}>
            {I.back} <span>العودة للسواقين</span>
          </button>
          <h1>السائقين بانتظار الموافقة</h1>
          <p>{drivers.length} سائق معلق</p>
        </div>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : drivers.length === 0 ? (
        <div className="page-empty">
          {I.check}
          <h3>لا يوجد سائقين بانتظار الموافقة</h3>
          <p>جميع طلبات السائقين تمت مراجعتها</p>
        </div>
      ) : (
        <div className="pending-drivers-grid">
          {drivers.map(driver => (
            <div key={driver.id} className="pending-driver-card">
              <div className="driver-card-header">
                <div className="driver-avatar">{driver.full_name?.charAt(0)}</div>
                <div className="driver-basic-info">
                  <h3>{driver.full_name}</h3>
                  <p dir="ltr">{driver.phone}</p>
                </div>
              </div>
              
              <div className="driver-card-details">
                <div className="detail-row">
                  <span className="detail-label">البريد:</span>
                  <span className="detail-value">{driver.email || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">رقم الهوية:</span>
                  <span className="detail-value">{driver.national_id || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">رخصة القيادة:</span>
                  <span className="detail-value">{driver.driver_license_number || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">الوثائق:</span>
                  <span className="detail-value">{driver.documents_count} وثيقة</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">تاريخ التقديم:</span>
                  <span className="detail-value">{driver.created_at ? new Date(driver.created_at).toLocaleDateString('ar-SA') : '-'}</span>
                </div>
              </div>

              <div className="driver-card-actions">
                <button className="btn btn--ghost btn--sm" onClick={() => navigate(`/tenant/drivers/${driver.id}`)}>
                  {I.eye} عرض التفاصيل
                </button>
                <div className="action-buttons">
                  <button className="btn btn--success btn--sm" onClick={() => openActionModal(driver, 'approve')}>
                    {I.check} قبول
                  </button>
                  <button className="btn btn--danger btn--sm" onClick={() => openActionModal(driver, 'reject')}>
                    {I.x} رفض
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showModal && selectedDriver && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{actionType === 'approve' ? 'قبول السائق' : 'رفض السائق'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <div className="modal-body">
              <div className="confirmation-info">
                <div className="driver-avatar large">{selectedDriver.full_name?.charAt(0)}</div>
                <h3>{selectedDriver.full_name}</h3>
                <p>{selectedDriver.phone}</p>
              </div>
              
              {actionType === 'approve' ? (
                <div className="alert alert--success">
                  سيتم تفعيل حساب السائق وسيتمكن من استقبال الطلبات
                </div>
              ) : (
                <div className="alert alert--danger">
                  سيتم رفض طلب السائق ولن يتمكن من استخدام التطبيق
                </div>
              )}
              
              <div className="form-group">
                <label>ملاحظات {actionType === 'reject' ? '(سبب الرفض)' : '(اختياري)'}</label>
                <textarea 
                  value={notes} 
                  onChange={e => setNotes(e.target.value)}
                  placeholder={actionType === 'reject' ? 'اكتب سبب الرفض...' : 'أي ملاحظات إضافية...'}
                  rows="3"
                  required={actionType === 'reject'}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
              <button 
                className={`btn btn--${actionType === 'approve' ? 'success' : 'danger'}`}
                onClick={handleAction}
                disabled={submitting || (actionType === 'reject' && !notes.trim())}
              >
                {submitting ? 'جاري المعالجة...' : actionType === 'approve' ? 'تأكيد القبول' : 'تأكيد الرفض'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pending-drivers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 1.5rem;
        }
        .pending-driver-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .driver-card-header {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .driver-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary), #60a5fa);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .driver-avatar.large {
          width: 72px;
          height: 72px;
          font-size: 2rem;
          margin: 0 auto;
        }
        .driver-basic-info h3 {
          margin: 0 0 4px;
          font-size: 1.1rem;
        }
        .driver-basic-info p {
          margin: 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }
        .driver-card-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--background);
          border-radius: 8px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
        }
        .detail-label {
          color: var(--text-secondary);
        }
        .detail-value {
          font-weight: 500;
        }
        .driver-card-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.5rem;
          border-top: 1px solid var(--border);
        }
        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }
        .btn--success {
          background: #22c55e;
          color: white;
        }
        .btn--success:hover {
          background: #16a34a;
        }
        .btn--danger {
          background: #ef4444;
          color: white;
        }
        .btn--danger:hover {
          background: #dc2626;
        }
        .confirmation-info {
          text-align: center;
          padding: 1rem 0;
        }
        .confirmation-info h3 {
          margin: 1rem 0 4px;
        }
        .confirmation-info p {
          margin: 0;
          color: var(--text-secondary);
        }
        .alert {
          padding: 1rem;
          border-radius: 8px;
          margin: 1rem 0;
          font-size: 0.9rem;
        }
        .alert--success {
          background: #f0fdf4;
          border: 1px solid #22c55e;
          color: #166534;
        }
        .alert--danger {
          background: #fef2f2;
          border: 1px solid #ef4444;
          color: #991b1b;
        }
        @media (max-width: 600px) {
          .pending-drivers-grid {
            grid-template-columns: 1fr;
          }
          .driver-card-actions {
            flex-direction: column;
            gap: 0.75rem;
          }
          .action-buttons {
            width: 100%;
          }
          .action-buttons .btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PendingDriversPage;
